<script setup>
import Column from 'primevue/column';
import { useDataTableStore } from '@stores/useDataTableStore';
import { trans } from '../../../_00_elements/translation';
import { useAccountStore } from '../store/account';
import UbiqTable from '../../../_01_atoms/components/UbiqDataTable.vue';
import { currency } from '../../../_00_elements/number-formatter';
import Tag from '../../../_01_atoms/components/Tag.vue';
import {
  getCompleteShorterReadableDate,
  getMonthDateFromIsoDate,
} from '../../../_00_elements/tools/date';
import CardEmptyTable from '../general/CardEmptyTable.vue';
import { BILLING_DOCUMENT_TYPES } from '../constants/constants';
import UbiqCreditNoteMenu from './menu/UbiqCreditNoteMenu.vue';
import UbiqInvoiceMenu from './menu/UbiqInvoiceMenu.vue';
import InvoiceStatusTag from './tags/InvoiceStatusTag.vue';

const props = defineProps({
  hiddenColumns: {
    type: Array,
    default: () => [],
  },
});

const accountStore = useAccountStore();
const datatableStore = useDataTableStore();

datatableStore.setDefaultOrder('billingDocument.createdAt', 'desc');

accountStore.setStripeAccountStatus(window.initialState.stripeAccountStatus);
</script>

<template>
  <UbiqTable data-key="row_id" scrollable>
    <Column
      v-if="!hiddenColumns.includes('amount')"
      field="billingDocument.amount"
      :header="trans('gestion.invoices.table.amount_ht')"
      sortable
      class="column--currency column--bold"
    >
      <template #body="{ data }">
        {{ currency(data.amount / 100) }}
      </template>
    </Column>

    <Column v-if="!hiddenColumns.includes('status')">
      <template #body="{ data }">
        <InvoiceStatusTag :status="data.status?.toLowerCase()" />
      </template>
    </Column>

    <Column
      v-if="!hiddenColumns.includes('type')"
      field="billingDocument.type"
      :header="trans('gestion.invoices.table.type')"
      sortable
    >
      <template #body="{ data }">
        {{ trans(`gestion.invoices.type.${data.invoice_type}`) }}
      </template>
    </Column>

    <Column
      v-if="!hiddenColumns.includes('issuedDate')"
      field="billingDocument.issuedDate"
      :header="trans('gestion.invoices.table.period')"
      sortable
    >
      <template #body="{ data }">
        <Tag
          v-if="data.issued_date"
          text-color="black"
          background-color="gray-light"
          class="invoice-date"
        >
          {{ getMonthDateFromIsoDate(data.issued_date.iso) }}
        </Tag>
        <span v-else>-</span>
      </template>
    </Column>

    <Column
      v-if="!hiddenColumns.includes('invoiceNumber')"
      field="billingDocument.invoiceNumber"
      sortable
      :header="trans('gestion.invoices.table.invoice_number')"
    >
      <template #body="{ data }">
        {{ data.invoice_number }}
      </template>
    </Column>

    <Column
      v-if="!hiddenColumns.includes('createdAt')"
      field="billingDocument.createdAt"
      :header="trans('gestion.invoices.table.created_date')"
      sortable
    >
      <template #body="{ data }">
        {{ getCompleteShorterReadableDate(data.created_at.iso) }}
      </template>
    </Column>

    <Column frozen align-frozen="right">
      <template #body="{ data }">
        <div class="simple-table__column--actions">
          <UbiqInvoiceMenu v-if="data.invoice_type === BILLING_DOCUMENT_TYPES.INVOICE" :invoice="data" />
          <UbiqCreditNoteMenu v-if="data.invoice_type === BILLING_DOCUMENT_TYPES.CREDIT_NOTE" :invoice="data" />
        </div>
      </template>
    </Column>

    <template #empty-state>
      <CardEmptyTable
        logo="ph_coins_blue"
        :title="trans('gestion.invoice.empty.title')"
        :subtitle="trans('gestion.invoice.empty.subtitle')"
      />
    </template>
  </UbiqTable>
</template>
